import { useEffect, useState } from 'react';
import FormLabel from '@mui/material/FormLabel';
import Slider, { SliderProps } from '@mui/material/Slider';
import { useAtom } from 'jotai';
import * as state from '@/state';
import { denormalizeSliderValues, normalizeSliderValues, updateUrl } from '@/utils';
import { ContrastSliderTitleAndResetIcon } from './styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { DEFAULT_CONTRAST, SliderType } from '@/constants';
import { Box } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import queryString from 'query-string';

type ContrastSliderProps = {
  title: string;
  disabled: boolean;
} & SliderProps;

const valuetext = (value: number) => {
  return `${value}`;
};

export const ContrastSlider = ({ title, disabled, ...props }: ContrastSliderProps) => {
  const [contrast, setContrast] = useAtom<number>(state.contrast);
  const [unfinishedContrast, setUnfinishedContrast] = useState<number>(
    denormalizeSliderValues(contrast, SliderType.CONTRAST)
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      setContrast(normalizeSliderValues(unfinishedContrast, SliderType.CONTRAST));
    }, 200);
    return () => clearTimeout(debounce);
  }, [setContrast, unfinishedContrast]);

  const handleContrastChanged = (event: Event, value: number | number[]) => {
    setUnfinishedContrast(value as number);
    updateUrl('contrastLevel', String(value));
  };
  useEffect(() => {
    const query = window.location.search;
    const parsed = queryString.parse(query, { arrayFormat: 'comma' });
    if (parsed.contrastLevel) {
      const contrastValue = JSON.parse(parsed.contrastLevel.toString());
      setUnfinishedContrast(contrastValue);
    }
  }, []);
  const handleReset = () => {
    setUnfinishedContrast(denormalizeSliderValues(DEFAULT_CONTRAST, SliderType.CONTRAST));
  };

  return (
    <Box>
      <ContrastSliderTitleAndResetIcon>
        <FormLabel sx={{ color: disabled ? '#CDCDCF' : abyssColors.primary[300] }}>
          {title}
        </FormLabel>
        {disabled ? (
          <AbyssIcon
            iconName="refresh-right-square-disabled"
            pointer
            onClick={handleReset}
            style={{ width: '2.4rem', height: '2.4rem' }}
          ></AbyssIcon>
        ) : (
          <AbyssIcon
            iconName="refresh-right-square"
            pointer
            onClick={handleReset}
            style={{ width: '2.4rem', height: '2.4rem' }}
          ></AbyssIcon>
        )}
      </ContrastSliderTitleAndResetIcon>
      <Slider
        {...props}
        aria-label="Contrast"
        getAriaValueText={valuetext}
        valueLabelDisplay="auto"
        value={unfinishedContrast}
        onChange={handleContrastChanged}
        size="small"
        sx={{ color: disabled ? '#CDCDCF' : abyssColors.primary[500] }}
        disabled={disabled}
      />
    </Box>
  );
};
